import { gql } from '@apollo/client';

// Tapestry GraphQL operations (ENG-2789).
//
// These target the Tapestry job-status API exposed by the api repo
// (`tapestryJobStatus` query + lifecycle mutations). Field names follow the
// schema's snake_case convention. `current_stage` and `completion_outcome` are
// plain Strings on the schema (no enum); the value sets the UI branches on live
// in `stages.ts` (TapestryStage / TapestryCompletionOutcome).
//
// `overall_progress` / `completion_outcome` / `completion_message` and the
// File-level `tapestry_enabled` / `tapestry_stage` flags are all live on the
// deployed schema (ENG-2788/2800), so the fragment selects them directly.

export const TAPESTRY_STAGE_STATUS_FRAGMENT = gql`
  fragment TapestryStageStatusFields on TapestryStageStatus {
    status
    progress_percentage
    error_message
    timing {
      started_at
      completed_at
    }
    sheets_processed
    rows_processed
    loans_classified
    regions_detected
    fields_mapped
    loans_validated
    errors_count
    loans_persisted
    ready_for_review
  }
`;

export const TAPESTRY_JOB_STATUS_FRAGMENT = gql`
  fragment TapestryJobStatusFields on TapestryJobStatus {
    file_id
    current_stage
    overall_progress
    completion_outcome
    completion_message
    decomposition {
      ...TapestryStageStatusFields
    }
    classification {
      ...TapestryStageStatusFields
    }
    detection {
      ...TapestryStageStatusFields
    }
    extraction {
      ...TapestryStageStatusFields
    }
    validation {
      ...TapestryStageStatusFields
    }
    persistence {
      ...TapestryStageStatusFields
    }
    review {
      ...TapestryStageStatusFields
    }
  }
  ${TAPESTRY_STAGE_STATUS_FRAGMENT}
`;

export const TAPESTRY_JOB_STATUS_QUERY = gql`
  query TapestryJobStatus($fileId: ID!) {
    tapestryJobStatus(file_id: $fileId) {
      ...TapestryJobStatusFields
    }
  }
  ${TAPESTRY_JOB_STATUS_FRAGMENT}
`;

export const CONFIRM_TAPESTRY_JOB = gql`
  mutation ConfirmTapestryJob($fileId: ID!) {
    confirmTapestryJob(file_id: $fileId) {
      file_id
      current_stage
    }
  }
`;

export const REJECT_TAPESTRY_JOB = gql`
  mutation RejectTapestryJob($fileId: ID!) {
    rejectTapestryJob(file_id: $fileId) {
      file_id
      current_stage
    }
  }
`;

export const RETRY_TAPESTRY_JOB = gql`
  mutation RetryTapestryJob($fileId: ID!) {
    retryTapestryJob(file_id: $fileId) {
      file_id
      current_stage
    }
  }
`;

export const CANCEL_TAPESTRY_JOB = gql`
  mutation CancelTapestryJob($fileId: ID!) {
    cancelTapestryJob(file_id: $fileId) {
      file_id
      current_stage
    }
  }
`;

// `corrections` is a JSONObject keyed by detected header → canonical field key
// (e.g. { "Loan ID": "account_id" }). The resolver merges it into the file's
// existing field_mapping, so callers only send the headers they changed.
export const SUBMIT_MAPPING_CORRECTIONS = gql`
  mutation SubmitMappingCorrections($fileId: ID!, $corrections: JSONObject!) {
    submitMappingCorrections(file_id: $fileId, corrections: $corrections) {
      file_id
      current_stage
    }
  }
`;
